import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type Point = { label: string; count: number };
interface EmailUsageReport {
  timeZone: string;
  totals: {
    today: number;
    last7Days: number;
    last30Days: number;
    thisMonth: number;
    thisYear: number;
    allTime: number;
    failedLast30Days: number;
    peakDay: { date: string; count: number } | null;
    since: string | null;
  };
  byDay: Point[];
  byWeek: Point[];
  byMonth: Point[];
  byYear: Point[];
  byKind: { kind: string; count: number }[];
}

type Range = 'day' | 'week' | 'month' | 'year';
const rangesFor = (noun: string): { id: Range; label: string; title: string }[] => [
  { id: 'day', label: 'Day', title: `${noun} per day, last 30 days` },
  { id: 'week', label: 'Week', title: `${noun} per week, last 12 weeks` },
  { id: 'month', label: 'Month', title: `${noun} per month, last 12 months` },
  { id: 'year', label: 'Year', title: `${noun} per year` },
];

/** What each kind of email is, in words. */
const KIND_LABELS: Record<string, string> = {
  alert: 'Alerts to parents',
  report: 'Reports',
  'family-invite': 'Family invites',
  'family-added': 'Added to a family',
  'account-invite': 'New account invites',
  'password-reset': 'Password resets',
  'setup-instructions': 'Setup instructions',
  'admin-signup': 'New sign-ups (to admins)',
  'admin-access-request': 'Access requests (to admins)',
  'admin-feedback': 'Feedback (to admins)',
  test: 'Test emails',
  'test-notification': 'Test notifications',
  other: 'Other',
};

const number = (n: number) => n.toLocaleString();
const dayName = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const monthName = (ym: string) => new Date(`${ym}-15T12:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
const pointLabel = (range: Range, label: string) =>
  range === 'day' ? dayName(label) : range === 'week' ? `Week of ${dayName(label)}` : range === 'month' ? monthName(label) : label;
const tickLabel = (range: Range, label: string) =>
  range === 'day' || range === 'week' ? dayName(label) : range === 'month' ? new Date(`${label}-15T12:00:00`).toLocaleDateString(undefined, { month: 'short' }) : label;

/**
 * Email usage (#378), under Admin → Email (SMTP): how many emails the server sends, so the email provider can be
 * sized. Counted per recipient, the way providers count.
 */
export function EmailUsage() {
  const [report, setReport] = useState<EmailUsageReport | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    api
      .get<EmailUsageReport>(`/api/admin/email/usage?tz=${encodeURIComponent(tz)}`)
      .then(setReport)
      .catch((e) => setError((e as Error).message));
  }, []);

  if (error) return <p className="error small">{error}</p>;
  if (!report) return <p className="muted small">Loading email usage…</p>;

  const t = report.totals;

  return (
    <div className="stack email-usage">
      <p className="muted small">
        Emails this server has sent, counted per recipient (the way email providers count them)
        {t.since ? `, since ${dayName(t.since)}` : ''}. Use it to choose a provider plan.
      </p>
      <div className="usage-tiles">
        <Tile label="Today" value={t.today} />
        <Tile label="Last 7 days" value={t.last7Days} />
        <Tile label="This month" value={t.thisMonth} />
        <Tile label="Last 30 days" value={t.last30Days} />
        <Tile label="This year" value={t.thisYear} />
        <Tile label="All time" value={t.allTime} />
      </div>
      <p className="muted small">
        {t.peakDay ? `Busiest day: ${dayName(t.peakDay.date)} with ${number(t.peakDay.count)}. ` : ''}
        {t.failedLast30Days ? (
          <span className="usage-failed">{number(t.failedLast30Days)} failed to send in the last 30 days (not counted above).</span>
        ) : (
          'No failed sends in the last 30 days.'
        )}
      </p>

      <UsageBars report={report} noun="Emails" />

      {report.byKind.length > 0 && (
        <table className="usage-kinds">
          <caption className="muted small">By kind, last 30 days</caption>
          <tbody>
            {report.byKind.map((k) => (
              <tr key={k.kind}>
                <td>{KIND_LABELS[k.kind] ?? k.kind}</td>
                <td className="num">{number(k.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Counts over time with a Day / Week / Month / Year switch (#378, also the sign-in report #393). */
export function UsageBars({ report, noun }: { report: Pick<EmailUsageReport, 'byDay' | 'byWeek' | 'byMonth' | 'byYear'>; noun: string }) {
  const [range, setRange] = useState<Range>('day');
  const ranges = rangesFor(noun);
  const series = range === 'day' ? report.byDay : range === 'week' ? report.byWeek : range === 'month' ? report.byMonth : report.byYear;
  const max = Math.max(1, ...series.map((p) => p.count));
  const every = series.length > 14 ? 5 : series.length > 8 ? 2 : 1; // which bars get an axis label
  const active = ranges.find((r) => r.id === range)!;
  return (
    <>
      <div className="row-between">
        <strong className="small">{active.title}</strong>
        <div className="segmented" role="radiogroup" aria-label="Group by">
          {ranges.map((r) => (
            <button key={r.id} role="radio" aria-checked={range === r.id} className={range === r.id ? 'active' : ''} onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {series.length === 0 ? (
        <p className="muted small">Nothing yet.</p>
      ) : (
        <div className="usage-chart" role="list" aria-label={active.title}>
          {series.map((p, i) => (
            <div key={p.label} className="usage-col" role="listitem" aria-label={`${pointLabel(range, p.label)}: ${number(p.count)}`}>
              <div className="usage-bar-wrap">
                <span className="usage-tip">
                  {pointLabel(range, p.label)}: <strong>{number(p.count)}</strong>
                </span>
                <div className="usage-bar" style={{ height: `${(p.count / max) * 100}%` }} />
              </div>
              <span className="usage-tick">{i % every === 0 || i === series.length - 1 ? tickLabel(range, p.label) : ''}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="usage-tile">
      <span className="usage-value">{number(value)}</span>
      <span className="muted small">{label}</span>
    </div>
  );
}
